import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from 'react'
import { Save, Zap } from 'lucide-react'
import { toast } from 'sonner'

import { PosCheckoutPayment } from './pos-checkout-payment'
import { PosInvoicePopup } from './pos-invoice-popup'
import {
    posMaterialEffective,
    posOrderItemIndex,
    posOrderTotals,
} from './pos-order-payload'
import type { OrderTab } from './pos-types'
import type { PosCheckoutMode } from '@/hooks/use-pos-checkout'
import type { InvoiceBuyerFormValue } from '@/components/forms/invoice-buyer-fields'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import {
    DEFAULT_CREATE_INVOICE_BUYER,
    InvoiceBuyerFields,
    applyBuyerPatch,
    buildInvoiceBuyerPayload,
} from '@/components/forms/invoice-buyer-fields'
import { usePosCheckout } from '@/hooks/use-pos-checkout'
import { useAuth } from '@/hooks/use-auth'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { formatVND } from '@/lib/format'
import { twJoin } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'
import { posthog } from '@/lib/posthog'
import { DEFAULT_SHIPPING_CARRIER_ID, WALKIN_CUSTOMER } from '@/lib/constants'

/** Cách khách trả tại quầy (đo lường M-175). */
function paymentSplit(cash: number, transfer: number) {
    if (cash > 0 && transfer > 0) return 'mixed'
    if (transfer > 0) return 'bank_transfer'
    if (cash > 0) return 'cash'
    return 'none'
}

export interface PosQuickCheckoutHandle {
    submit: () => void
}

interface Props {
    workflow?: ReturnType<typeof useWorkflowTracking>
    tab: OrderTab
    onSuccess: () => void
    /** Chiết khấu đơn thuộc tab (parent giữ) — dòng "Tổng tiền hàng" bên trái
     *  cũng đọc để tính cùng số VAT. */
    onOrderDiscountChange: (percent: number) => void
    /** Called immediately after the order is created (before invoice popup). Use to show QR in parent. */
    onOrderCreated?: (orderId: string, method: 'cash' | 'bank_transfer') => void
}

/**
 * Inline checkout panel for "Bán nhanh" (Quick Sale) mode.
 * Renders payment section directly in the right sidebar — no modal.
 */
export const PosQuickCheckoutPanel = forwardRef<PosQuickCheckoutHandle, Props>(
    function PosQuickCheckoutPanel(
        {
            tab,
            onSuccess,
            onOrderDiscountChange,
            onOrderCreated,
            workflow: parentWorkflow,
        },
        ref,
    ) {
        const localWorkflow = useWorkflowTracking('pos_quick_checkout', {
            attemptKey: tab.id,
            active: !parentWorkflow,
            retainAttempts: true,
        })
        const workflow = parentWorkflow ?? localWorkflow
        const checkout = usePosCheckout()
        const { hasPermission } = useAuth()
        const canEditDiscount = hasPermission('sales_orders.edit_discount')
        const { data: invoiceSettings } = useInvoiceSettings()
        const vatPercent = invoiceSettings?.vat_percent ?? 0

        const orderDiscountPercent = tab.orderDiscountPercent ?? 0

        // Tiền vật tư (giá vật tư) — chịu chiết khấu dòng như dòng hàng, gộp vào
        // base cùng SP TRƯỚC chiết khấu đơn + VAT. Dòng GỘP → giá vật tư hiệu lực 0.
        const { priceOf: materialPriceOf } = posMaterialEffective(
            tab.items,
            tab.packagingItems,
        )
        const {
            itemSubtotal,
            materialTotal,
            baseAmount,
            subtotal,
            vatAmount,
            totalAmount,
        } = posOrderTotals(
            tab.items,
            tab.packagingItems,
            orderDiscountPercent,
            vatPercent,
        )
        const itemCount = tab.items.reduce((s, i) => s + i.quantity, 0)

        const [customerPhone, setCustomerPhone] = useState(
            tab.customer?.phone ?? '',
        )
        // HĐĐT: đơn quầy tự nhảy "đã giao" sau khi bán, cron nháp hóa đơn chỉ
        // nhặt đơn có tick issue_einvoice — POS phải gửi tick như form chuẩn.
        const einvoiceEnabled = useFeatureFlag('dev-einvoice')
        const [invoiceBuyer, setInvoiceBuyer] = useState<InvoiceBuyerFormValue>(
            DEFAULT_CREATE_INVOICE_BUYER,
        )
        // Mã đơn nội bộ (đối chiếu sổ tay / hệ thống ngoài) — tuỳ chọn.
        const [internalCode, setInternalCode] = useState('')
        // "Trừ vào số dư khách": chỉ hiện khi khách đang dư (current_debt < 0).
        const customerCredit = Math.max(0, -(tab.customer?.current_debt ?? 0))
        const [applyCredit, setApplyCredit] = useState(false)
        // Lỗi SĐT chỉ hiện sau khi bấm Lưu đơn / Bán ngay (nút không khoá vì
        // form chưa hợp lệ — rule "Form Submit & Validation" trong AGENTS.md).
        const [showPhoneError, setShowPhoneError] = useState(false)

        // Sync phone when customer changes
        useEffect(() => {
            setCustomerPhone(tab.customer?.phone ?? '')
            setApplyCredit(false)
            setShowPhoneError(false)
        }, [tab.customer?.id, tab.customer?.phone])

        useEffect(() => {
            setInvoiceBuyer(DEFAULT_CREATE_INVOICE_BUYER)
            setInternalCode('')
            setApplyCredit(false)
        }, [tab.id])
        // M-180: mặc định một ô số tiền + chọn hình thức (kiểu KiotViet);
        // "Thanh toán kết hợp" (M-175) mới tách hai ô tiền mặt / chuyển khoản.
        // Một hình thức: `customerPayment` là toàn bộ số khách đưa bằng
        // `paymentChoice`; kết hợp: `customerPayment` là tiền mặt.
        const [combined, setCombined] = useState(false)
        const [paymentChoice, setPaymentChoice] = useState<
            'cash' | 'bank_transfer'
        >('cash')
        const [customerPayment, setCustomerPayment] = useState(totalAmount)
        const [transferAmount, setTransferAmount] = useState(0)
        const [overpayAction, setOverpayAction] = useState<'change' | 'credit'>(
            'change',
        )
        const [invoiceOrder, setInvoiceOrder] = useState<{
            id: string
            code: string
            method: 'cash' | 'bank_transfer'
            issueEInvoice: boolean
        } | null>(null)

        // Số khách phải trả tại quầy = tổng − credit cấn (nếu tick).
        const creditApplied = applyCredit
            ? Math.min(customerCredit, totalAmount)
            : 0
        const amountDue = Math.round(totalAmount - creditApplied)

        // Đổi tab / giỏ / credit → về một hình thức Tiền mặt, thu đủ.
        useEffect(() => {
            setCombined(false)
            setPaymentChoice('cash')
            setCustomerPayment(amountDue)
            setTransferAmount(0)
            setOverpayAction('change')
        }, [tab.id, amountDue])

        // Vào / ra "Thanh toán kết hợp": số tiền đặt lại = phải trả ở Tiền
        // mặt, để không mang số của chế độ trước sang ghi nhầm hình thức.
        const handleCombinedChange = (v: boolean) => {
            setCombined(v)
            setPaymentChoice('cash')
            setCustomerPayment(amountDue)
            setTransferAmount(0)
            setOverpayAction('change')
        }

        // Nhập số chuyển khoản → ô tiền mặt tự còn phần còn lại, NHƯNG chỉ khi
        // ô tiền mặt vẫn đang là số tự điền: thu ngân đã gõ tiền mặt thì giữ
        // nguyên (thiếu thì thành nợ, dư thì trả lại / ghi số dư).
        const handleTransferChange = (v: number) => {
            if (customerPayment === Math.max(0, amountDue - transferAmount)) {
                setCustomerPayment(Math.max(0, amountDue - v))
            }
            setTransferAmount(v)
        }
        // Tiền khách đưa theo từng hình thức. Một hình thức: cả ô thuộc hình
        // thức đã chọn (chọn Chuyển khoản → không có đồng tiền mặt nào).
        const cashPaid =
            combined || paymentChoice === 'cash' ? customerPayment : 0
        const transferPaid = combined
            ? transferAmount
            : paymentChoice === 'bank_transfer'
              ? customerPayment
              : 0
        const tendered = cashPaid + transferPaid
        const shortfall = amountDue - tendered
        // Hình thức chính (phiếu in, màn hình khách, đo lường): một hình thức
        // thì theo nút tròn; kết hợp mà chỉ chuyển khoản thì là chuyển khoản,
        // còn lại tiền mặt.
        const paymentMethod: 'cash' | 'bank_transfer' = !combined
            ? paymentChoice
            : transferPaid > 0 && cashPaid <= 0
              ? 'bank_transfer'
              : 'cash'

        const hasCustomer = tab.customer !== null
        // Walk-in fallback: no selection => anonymous walk-in customer.
        // Phone field is optional in this case (uses walk-in seed phone).
        const phoneOk = hasCustomer ? customerPhone.trim().length >= 8 : true

        // Đơn chỉ vật tư có giá (bán túi/hộp không kèm chè) vẫn bán được.
        const hasSellable = tab.items.length > 0 || materialTotal > 0
        // Nút CHỈ khoá khi đang gửi request (rule "Form Submit & Validation"
        // trong AGENTS.md); SĐT sai / thu thiếu khách lẻ báo lỗi khi bấm.
        const canSubmit = hasSellable && !checkout.isPending

        const handleSubmit = async (mode: PosCheckoutMode = 'pay_now') => {
            if (!canSubmit) {
                workflow.step('validation_failed')
                return
            }
            if (!phoneOk) {
                setShowPhoneError(true)
                workflow.step('validation_failed')
                return
            }
            // Thu thiếu → phần còn lại thành nợ của đơn: khách lẻ không có
            // tài khoản công nợ (server cũng chặn).
            if (mode === 'pay_now' && shortfall > 0.005 && !hasCustomer) {
                toast.error(m.customer_select_for_debt())
                workflow.step('validation_failed')
                return
            }
            const buyer = tab.customer ?? WALKIN_CUSTOMER
            const phoneToSend =
                customerPhone.trim() || WALKIN_CUSTOMER.phone || ''
            try {
                const order = await workflow.run(
                    () =>
                        checkout.mutateAsync({
                            order: {
                                source_channel: 'direct',
                                customer_id: buyer.id,
                                customer_name: buyer.name,
                                customer_phone: phoneToSend,
                                shipping_address:
                                    buyer.address || m.customer_in_store(),
                                product_notes: tab.note || undefined,
                                // Đơn quầy luôn "Tự giao" (M-8: bỏ ô chọn hãng
                                // trên giao diện POS). Vẫn gửi id để phiếu in /
                                // báo cáo đọc hãng vận chuyển như trước.
                                shipping_carrier_id:
                                    DEFAULT_SHIPPING_CARRIER_ID,
                                issue_einvoice: einvoiceEnabled
                                    ? invoiceBuyer.enabled
                                    : false,
                                invoice_buyer: einvoiceEnabled
                                    ? buildInvoiceBuyerPayload(
                                          invoiceBuyer,
                                          'create',
                                      )
                                    : undefined,
                                internal_order_code:
                                    internalCode.trim() || undefined,
                                apply_credit: applyCredit && customerCredit > 0,
                                // VAT snapshot theo giá trị POS đang hiển thị — đổi thuế
                                // suất global sau này không làm đơn cũ lệch.
                                vat_percent: vatPercent,
                                vat_amount: vatAmount,
                                discount_percent:
                                    orderDiscountPercent > 0
                                        ? orderDiscountPercent
                                        : undefined,
                                items: tab.items.map((item) => ({
                                    product_id: item.product_id,
                                    quantity: item.quantity,
                                    unit_price: item.unit_price,
                                    discount_percent: item.discount_percent,
                                    material_price_split:
                                        item.material_price_split || undefined,
                                })),
                                packaging_items: (tab.packagingItems ?? [])
                                    .filter((p) => p.quantity > 0)
                                    .map((p) => ({
                                        packaging_material_id:
                                            p.packaging_material_id,
                                        quantity: p.quantity,
                                        source: p.source,
                                        // Dòng GỘP gửi giá 0 (giá vật tư trong giá SP).
                                        unit_price: materialPriceOf(p),
                                        discount_percent: p.discount_percent,
                                        product_id: p.product_id || undefined,
                                        // POS: line_key = product_id → index dòng đơn để
                                        // BE lưu order_item_id (hiển thị vật tư theo dòng).
                                        order_item_index: posOrderItemIndex(
                                            tab.items,
                                            p.line_key,
                                        ),
                                    })),
                            },
                            mode,
                            paymentMethod,
                            // Chuyển khoản thu như tiền mặt (M-8): số tiền thu
                            // ngân nhập là số thật, không ép về 0 nữa.
                            paymentAmount: tendered,
                            // M-175: tiền khách đưa theo từng hình thức; thiếu
                            // thì phần còn lại thành nợ của đơn.
                            payments: [
                                ...(cashPaid > 0
                                    ? [
                                          {
                                              method: 'cash' as const,
                                              amount: cashPaid,
                                          },
                                      ]
                                    : []),
                                ...(transferPaid > 0
                                    ? [
                                          {
                                              method: 'bank_transfer' as const,
                                              amount: transferPaid,
                                          },
                                      ]
                                    : []),
                            ],
                            overpayAction,
                            // "Tính vào công nợ": phần đưa dư thành số dư khách.
                            overpayCredit:
                                overpayAction === 'credit' && hasCustomer
                                    ? Math.max(0, tendered - amountDue)
                                    : 0,
                        }),
                    (result) => result.id,
                )

                posthog.capture('pos_quick_checkout_completed', {
                    order_id: order.id,
                    mode,
                    payment_method: paymentMethod,
                    // M-175 (docs/analytics/quick-sale-split-payment.md):
                    // khách trả kết hợp / để nợ — không gửi số tiền khách đưa.
                    payment_split: paymentSplit(cashPaid, transferPaid),
                    debt_left: mode === 'pay_now' && shortfall > 0.005,
                    total_amount: totalAmount,
                    item_count: tab.items.length,
                })

                // Bán xong (cả Lưu đơn lẫn Bán ngay) sale page giữ nguyên tab nên
                // phải tự dọn: tick hóa đơn + người mua dính sang đơn sau thì
                // "Bán ngay" kế tiếp phát hành hóa đơn chính thức nhầm (M-167).
                setInvoiceBuyer(DEFAULT_CREATE_INVOICE_BUYER)
                setInternalCode('')
                setApplyCredit(false)
                // M-180: đơn sau mở lại ở một hình thức Tiền mặt.
                handleCombinedChange(false)
                if (mode === 'save') {
                    onSuccess()
                } else {
                    onOrderCreated?.(order.id, paymentMethod)
                    setInvoiceOrder({
                        id: order.id,
                        code: order.order_code,
                        method: paymentMethod,
                        // M-167: tích hóa đơn + Bán ngay → popup tự phát hành.
                        issueEInvoice: order.issue_einvoice === true,
                    })
                }
            } catch {
                // error handled by mutation hook
            }
        }

        const handleSubmitRef = useRef(handleSubmit)
        handleSubmitRef.current = handleSubmit
        useImperativeHandle(
            ref,
            () => ({ submit: () => handleSubmitRef.current('pay_now') }),
            [],
        )

        return (
            <>
                <div
                    onFocusCapture={() => workflow.interact('details')}
                    className="flex-1 flex flex-col min-h-0"
                >
                    <div
                        data-testid="pos-quick-checkout-scroll"
                        className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3 text-sm"
                    >
                        {/* Order summary */}
                        <div className="space-y-1.5">
                            <div className="flex justify-between">
                                <span className="text-muted-foreground">
                                    {m.order_items_subtotal()}
                                    <span className="ml-2 text-xs">
                                        ({itemCount})
                                    </span>
                                </span>
                                <span className="font-medium">
                                    {formatVND(itemSubtotal)}
                                </span>
                            </div>
                            {canEditDiscount && (
                                <div className="flex items-center justify-between gap-2">
                                    <label
                                        htmlFor="pos-quick-order-discount"
                                        className="text-muted-foreground shrink-0"
                                    >
                                        {m.order_discount_percent()}
                                    </label>
                                    <div className="flex items-center gap-1">
                                        <input
                                            id="pos-quick-order-discount"
                                            type="number"
                                            min={0}
                                            max={100}
                                            step={0.5}
                                            value={orderDiscountPercent}
                                            onChange={(e) =>
                                                onOrderDiscountChange(
                                                    Math.min(
                                                        100,
                                                        Math.max(
                                                            0,
                                                            Number(
                                                                e.target.value,
                                                            ),
                                                        ),
                                                    ),
                                                )
                                            }
                                            className="w-14 text-center border-b border-dashed border-primary/50 bg-transparent text-sm focus:outline-none focus:border-primary"
                                        />
                                        <span className="text-xs text-muted-foreground">
                                            %
                                        </span>
                                    </div>
                                </div>
                            )}
                            {orderDiscountPercent > 0 && (
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {m.order_discount_label({
                                            percent:
                                                String(orderDiscountPercent),
                                        })}
                                    </span>
                                    <span className="text-destructive">
                                        -{formatVND(baseAmount - subtotal)}
                                    </span>
                                </div>
                            )}
                            {materialTotal > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        {m.order_material_total()}
                                    </span>
                                    <span>{formatVND(materialTotal)}</span>
                                </div>
                            )}
                            {vatPercent > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">
                                        {m.order_vat_label({
                                            percent: String(vatPercent),
                                        })}
                                    </span>
                                    <span>{formatVND(vatAmount)}</span>
                                </div>
                            )}
                        </div>

                        {creditApplied > 0 && (
                            <div className="flex justify-between text-sm">
                                <span className="text-muted-foreground">
                                    {m.order_credit_apply_label()}
                                </span>
                                <span className="text-green-700">
                                    -{formatVND(creditApplied)}
                                </span>
                            </div>
                        )}

                        {/* Amount due */}
                        <div className="flex justify-between items-center pt-1 border-t">
                            <span className="font-semibold">
                                {m.customer_need_to_pay()}
                            </span>
                            <span className="text-primary text-lg font-bold">
                                {formatVND(amountDue)}
                            </span>
                        </div>

                        {!hasCustomer && tab.items.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                                {m.customer_walk_in_fallback({
                                    walkIn: m.customer_walk_in(),
                                })}
                            </p>
                        )}

                        {/* Customer phone — required only when a customer is picked */}
                        {hasCustomer && (
                            <div>
                                <label className="text-xs text-muted-foreground mb-1 block">
                                    {m.customer_phone_label()} *
                                </label>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    autoComplete="tel"
                                    value={customerPhone}
                                    onChange={(e) =>
                                        setCustomerPhone(e.target.value)
                                    }
                                    minLength={8}
                                    required
                                    aria-invalid={showPhoneError && !phoneOk}
                                    aria-describedby={
                                        showPhoneError && !phoneOk
                                            ? 'pos-quick-phone-error'
                                            : undefined
                                    }
                                    className={twJoin(
                                        'w-full rounded border bg-background px-2 py-1.5 text-sm',
                                        showPhoneError && !phoneOk
                                            ? 'border-destructive'
                                            : 'border-input',
                                    )}
                                />
                                {showPhoneError && !phoneOk && (
                                    <p
                                        id="pos-quick-phone-error"
                                        role="alert"
                                        className="mt-1 text-xs text-destructive"
                                    >
                                        {m.validation_phone_min()}
                                    </p>
                                )}
                            </div>
                        )}

                        {/* Xuất HĐĐT + người mua công ty — cùng widget với form
                            bán hàng chuẩn. Bật tick → tự đổ MST/địa chỉ từ hồ
                            sơ khách (khách lẻ walk-in thì để trống). */}
                        {einvoiceEnabled && (
                            <InvoiceBuyerFields
                                idPrefix="pos-quick"
                                counterSale
                                value={invoiceBuyer}
                                onChange={(patch) =>
                                    setInvoiceBuyer((prev) =>
                                        applyBuyerPatch(
                                            prev,
                                            patch,
                                            tab.customer,
                                        ),
                                    )
                                }
                            />
                        )}

                        <div>
                            <label
                                htmlFor="pos-quick-internal-code"
                                className="text-xs text-muted-foreground mb-1 block"
                            >
                                {m.order_internal_code_label()}
                            </label>
                            <input
                                id="pos-quick-internal-code"
                                value={internalCode}
                                maxLength={64}
                                onChange={(e) =>
                                    setInternalCode(e.target.value)
                                }
                                placeholder={m.order_internal_code_placeholder()}
                                className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
                            />
                        </div>

                        {/* Trừ vào số dư khách — chỉ hiện khi khách đang dư. */}
                        {customerCredit > 0 && (
                            <label className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 p-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={applyCredit}
                                    onChange={(e) =>
                                        setApplyCredit(e.target.checked)
                                    }
                                    className="size-4 accent-primary"
                                />
                                <span className="font-medium text-green-800">
                                    {m.order_credit_apply_label()} (
                                    {formatVND(customerCredit)})
                                </span>
                            </label>
                        )}
                    </div>

                    {/* Khối thanh toán + nút bán — luôn dính đáy (FD-396): khối
                        lô dài không được đẩy nút bán khuất khỏi màn hình. */}
                    <div
                        data-testid="pos-quick-checkout-footer"
                        className="shrink-0 border-t bg-background"
                    >
                        <div className="p-3 pb-2">
                            <PosCheckoutPayment
                                amountDue={amountDue}
                                customerPayment={customerPayment}
                                onCustomerPaymentChange={setCustomerPayment}
                                overpayAction={overpayAction}
                                onOverpayActionChange={setOverpayAction}
                                hasCustomer={hasCustomer}
                                paymentMethod={paymentChoice}
                                onPaymentMethodChange={setPaymentChoice}
                                transferAmount={transferAmount}
                                onTransferAmountChange={handleTransferChange}
                                combined={combined}
                                onCombinedChange={handleCombinedChange}
                            />
                        </div>

                        <div className="p-2 border-t space-y-2">
                            {!hasSellable ? (
                                <div className="w-full rounded bg-muted text-muted-foreground text-center py-3 text-sm">
                                    {m.pos_add_products_to_order()}
                                </div>
                            ) : (
                                <div className="flex gap-2">
                                    {/* Left: Lưu đơn — create order status=new, staff
                                    handles confirm/assign/delivery from detail page */}
                                    <button
                                        type="button"
                                        onClick={() => handleSubmit('save')}
                                        disabled={!canSubmit}
                                        className={twJoin(
                                            'flex-1 flex flex-col items-center justify-center gap-1',
                                            'rounded-lg border border-slate-300 bg-slate-50 hover:bg-slate-100',
                                            'py-4 text-base font-semibold text-slate-800 transition-colors',
                                            'disabled:opacity-50 disabled:cursor-not-allowed',
                                        )}
                                    >
                                        <Save className="size-5" />
                                        {m.pos_save_order()}
                                    </button>
                                    {/* Right: Bán ngay — full cashier flow */}
                                    <button
                                        type="button"
                                        onClick={() => handleSubmit('pay_now')}
                                        disabled={!canSubmit}
                                        className={twJoin(
                                            'flex-1 flex flex-col items-center justify-center gap-1',
                                            'rounded-lg bg-primary hover:bg-primary/90',
                                            'py-4 text-base font-bold text-primary-foreground transition-colors',
                                            'disabled:opacity-50 disabled:cursor-not-allowed',
                                        )}
                                    >
                                        <Zap className="size-5" />
                                        {checkout.isPending
                                            ? m.common_processing()
                                            : m.pos_buy_now()}
                                        <span className="text-xs font-semibold opacity-90">
                                            {formatVND(amountDue)}
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {invoiceOrder && (
                    <PosInvoicePopup
                        open={true}
                        orderId={invoiceOrder.id}
                        orderCode={invoiceOrder.code}
                        paymentMethod={invoiceOrder.method}
                        issueEInvoice={invoiceOrder.issueEInvoice}
                        onDone={() => {
                            setInvoiceOrder(null)
                            onSuccess()
                        }}
                    />
                )}
            </>
        )
    },
)
